import { useState, useEffect } from 'react';
import {
  ActionableNotification,
  Modal,
  TextInput,
  Select,
  SelectItem,
  InlineLoading,
  InlineNotification,
  NumberInput,
  FormGroup,
} from '@carbon/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOntologies } from '@/lib/query/hooks/useOntologies';
import { useVersions } from '@/lib/query/hooks/useVersions';
import { useKnowledgeBases } from '@/lib/query/hooks';
import { explorationApi } from '@/lib/api/exploration.api';
import type { OntologyVersion } from '@/lib/api/version.api';
import { outcomeMessage, snapshotIds, useResilientMutation } from '@/lib/query/useResilientMutation';
import { verifyExplorationCreated, type CreateExplorationVars } from '@/lib/query/verifiers';
import { versionLabel, type KnowledgeExploration } from '@snxt/snxt-onto-share';

interface CreateExplorationModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (explorationId: string) => void;
}

/**
 * Modal for creating a new Knowledge Exploration.
 *
 * The user picks:
 *  1. An ontology + published version
 *  2. A knowledge base
 *  3. (optional) custom name, threshold, top-k
 *
 * On submit the exploration is created and the populate pipeline is
 * triggered immediately (populate: true).
 */
export function CreateExplorationModal({
  open,
  onClose,
  onCreated,
}: CreateExplorationModalProps) {
  const queryClient = useQueryClient();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [ontologyId, setOntologyId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [kbId, setKbId] = useState('');
  const [name, setName] = useState('');
  const [threshold, setThreshold] = useState<number>(0.5);
  const [topK, setTopK] = useState<number>(5);
  const [error, setError] = useState<string | null>(null);

  // ── Data ───────────────────────────────────────────────────────────────────
  const { data: ontologies, isLoading: ontologiesLoading } = useOntologies();
  const { data: versions, isLoading: versionsLoading } = useVersions(ontologyId || undefined);
  const { data: kbs, isLoading: kbsLoading } = useKnowledgeBases();

  // Filter to published versions only
  const publishedVersions: OntologyVersion[] = (versions ?? []).filter(
    (v: OntologyVersion) => v.status === 'published'
  );

  // A Knowledge Graph can only be built from a PUBLISHED ontology version.
  // When the picked ontology has none, guide the user to publish first —
  // otherwise the Create button is silently disabled and users don't connect
  // "no published version" with "go publish the ontology" (Feedback A3).
  const needsPublish = !!ontologyId && !versionsLoading && publishedVersions.length === 0;

  // Auto-select latest published version when ontology changes
  useEffect(() => {
    setVersionId('');
    if (publishedVersions.length === 1) {
      setVersionId(publishedVersions[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ontologyId, versions]);

  // Baseline for verification, and the reason it is a live query rather than a fetch at submit
  // time: the list page already holds this key, so opening the modal usually costs nothing, and
  // the baseline is in hand BEFORE the link has a chance to break.
  const { data: knownExplorations } = useQuery<KnowledgeExploration[]>({
    queryKey: ['explorations'],
    queryFn: () => explorationApi.list(),
    enabled: open,
    // Overrides the global 5 minute staleTime. A baseline is only as good as it is fresh: a list
    // from five minutes ago is missing rows created since, and a missing row is one this modal
    // would then read as "the one I just created".
    staleTime: 0,
  });

  // ── Mutation ───────────────────────────────────────────────────────────────
  // Create is asynchronous server-side (the row is inserted, populate is fired and forgotten,
  // 201 comes back in ~0.5s), so a "timeout" here almost always means the response was lost,
  // not that the work failed. Ask the server instead of accusing it.
  const createMutation = useResilientMutation<KnowledgeExploration, CreateExplorationVars>({
    mutationFn: (vars) => explorationApi.create(vars.dto),
    verify: verifyExplorationCreated,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['explorations'] });
      onCreated(res.id);
    },
  });

  const handleSubmit = () => {
    setError(null);
    if (!ontologyId || !versionId || !kbId) {
      setError('Please select an ontology, a published version, and a knowledge base.');
      return;
    }
    void createMutation.submit({
      dto: {
        ontology_id: ontologyId,
        ontology_version_id: versionId,
        knowledge_base_id: kbId,
        name: name.trim() || undefined,
        similarity_threshold: threshold,
        top_k_per_concept: topK,
        populate: true,
      },
      knownIds: snapshotIds(knownExplorations),
      snapshotAvailable: Array.isArray(knownExplorations),
    });
  };

  const handleClose = () => {
    setOntologyId('');
    setVersionId('');
    setKbId('');
    setName('');
    setThreshold(0.5);
    setTopK(5);
    setError(null);
    createMutation.reset();
    onClose();
  };

  // Covers the verification window too, which is the point: that window is where the second
  // click used to land and produce the duplicate.
  const isLoading = createMutation.isPending;
  const failure = outcomeMessage(createMutation.error);

  return (
    <Modal
      open={open}
      modalHeading="New Knowledge Exploration"
      primaryButtonText={
        createMutation.isVerifying
          ? 'Checking…'
          : isLoading
            ? 'Creating…'
            : 'Create & Populate'
      }
      secondaryButtonText="Cancel"
      primaryButtonDisabled={isLoading || !ontologyId || !versionId || !kbId}
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
      onSecondarySubmit={handleClose}
      size="sm"
    >
      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          style={{ marginBottom: '1rem' }}
        />
      )}

      {failure && (
        <ActionableNotification
          kind={createMutation.isUnconfirmed ? 'warning' : 'error'}
          title={createMutation.isUnconfirmed ? 'Not confirmed' : 'Error'}
          subtitle={failure}
          lowContrast
          hideCloseButton
          inline={false}
          actionButtonLabel="Retry"
          onActionButtonClick={() => void createMutation.retry()}
          data-testid="exploration-create-failure"
          style={{ marginBottom: '1rem' }}
        />
      )}

      {/* Ontology selector */}
      <Select
        id="exploration-ontology"
        labelText="Ontology *"
        value={ontologyId}
        onChange={(e) => setOntologyId(e.target.value)}
        disabled={isLoading}
        style={{ marginBottom: '1rem' }}
      >
        <SelectItem value="" text={ontologiesLoading ? 'Loading…' : 'Select an ontology'} />
        {(ontologies ?? []).filter((o) => o.id).map((o) => (
          <SelectItem key={o.id!} value={o.id!} text={o.name} />
        ))}
      </Select>

      {/* Published version selector */}
      <Select
        id="exploration-version"
        labelText="Published version *"
        value={versionId}
        onChange={(e) => setVersionId(e.target.value)}
        disabled={isLoading || !ontologyId}
        style={{ marginBottom: '1rem' }}
      >
        <SelectItem
          value=""
          text={
            !ontologyId
              ? 'Select an ontology first'
              : versionsLoading
                ? 'Loading versions…'
                : publishedVersions.length === 0
                  ? 'No published versions'
                  : 'Select a version'
          }
        />
        {publishedVersions.map((v) => (
          <SelectItem
            key={v.id}
            value={v.id}
            text={versionLabel(v)}
          />
        ))}
      </Select>

      {needsPublish && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Publish the ontology first"
          subtitle="A Knowledge Graph is built from a published ontology version. Open this ontology in the editor and click Publish, then come back here."
          data-testid="exploration-needs-publish"
          style={{ marginBottom: '1rem' }}
        />
      )}

      {/* Knowledge Base selector */}
      <Select
        id="exploration-kb"
        labelText="Knowledge Base *"
        value={kbId}
        onChange={(e) => setKbId(e.target.value)}
        disabled={isLoading}
        style={{ marginBottom: '1rem' }}
      >
        <SelectItem value="" text={kbsLoading ? 'Loading…' : 'Select a knowledge base'} />
        {(kbs ?? []).map((kb: { id: string; name: string }) => (
          <SelectItem key={kb.id} value={kb.id} text={kb.name} />
        ))}
      </Select>

      {/* Optional name */}
      <TextInput
        id="exploration-name"
        labelText="Name (optional)"
        placeholder="My exploration"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={isLoading}
        style={{ marginBottom: '1rem' }}
      />

      {/* Advanced settings */}
      <FormGroup legendText="Advanced settings" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <NumberInput
            id="exploration-threshold"
            label="Similarity threshold"
            min={0.1}
            max={0.99}
            step={0.05}
            value={threshold}
            onChange={(_e, { value }) => setThreshold(typeof value === 'number' ? value : 0.5)}
            disabled={isLoading}
            size="sm"
          />
          <NumberInput
            id="exploration-topk"
            label="Top-K per concept"
            min={1}
            max={20}
            step={1}
            value={topK}
            onChange={(_e, { value }) => setTopK(typeof value === 'number' ? value : 5)}
            disabled={isLoading}
            size="sm"
          />
        </div>
      </FormGroup>

      {isLoading && (
        <InlineLoading
          description={
            createMutation.isVerifying
              ? 'Connection problem. Checking with the server whether the exploration was created…'
              : 'Creating exploration and starting populate…'
          }
          style={{ marginTop: '1rem' }}
        />
      )}
    </Modal>
  );
}

// Made with Bob
